import React, { useCallback, useEffect, useState } from 'react';
import { styled } from 'baseui';
import { useAtomValue } from 'jotai';
import { useStageAtoms } from '../../../labelling-v2/components/stage-scope/hooks';
import { STAGE_EVENTS, TOOL } from '../../constants';
import { CSS_VARIABLE_STAGE_VISIBLE_SCALE } from '../../constants/stage-css-variables';
import useSnap from '../../providers/hooks/use-snap';
import useStage from '../../providers/hooks/use-stage';
import { type StagePointerEventT } from '../../providers/stage-provider';
import { isLabelingMode } from '../toolbar/utils';
import { getSnapHintPoint, getSnapPoint } from './utils';
const Line = styled<'line', {
  $color: string;
}>('line', ({
  $color
}) => ({
  transformBox: 'fill-box',
  strokeWidth: `var(${CSS_VARIABLE_STAGE_VISIBLE_SCALE})`,
  stroke: `${$color}`,
  strokeDasharray: '2,2'
}));
const Snapping = () => {
  const [hintLine, setHintLine] = useState<{
    pointX: number | undefined | null;
    pointY: number | undefined | null;
  }>({
    pointX: null,
    pointY: null
  });
  const {
    addInterceptor,
    removeInterceptor,
    stage,
    onStage
  } = useStage();
  const {
    selectedToolAtom,
    currentFrameDataAtom
  } = useStageAtoms();
  const selectedTool = useAtomValue(selectedToolAtom);
  const frameData = useAtomValue(currentFrameDataAtom);
  const {
    snapPoints
  } = useSnap(frameData);
  const updateMousePosWithSnapPos = useCallback((event: StagePointerEventT) => {
    const mousePos = event.detail.mousePos;
    const snapPos = getSnapPoint(snapPoints, mousePos);
    event.detail.mousePos[0] = snapPos.pointX ?? mousePos[0];
    event.detail.mousePos[1] = snapPos.pointY ?? mousePos[1];
    return event;
  }, [snapPoints]);
  const onPointerMove = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;
    const showSnap = getSnapHintPoint(snapPoints, mousePos);
    setHintLine(showSnap);
  }, [snapPoints]);
  const onDrag = useCallback((stageEvent: StagePointerEventT) => {
    const {
      mousePos
    } = stageEvent.detail;
    const showSnap = getSnapHintPoint(snapPoints, mousePos);
    setHintLine(showSnap);
  }, [snapPoints]);
  useEffect(() => {
    if (!stage) return;
    const stageEvents = [STAGE_EVENTS.POINTER_DOWN, STAGE_EVENTS.POINTER_MOVE, STAGE_EVENTS.POINTER_UP, STAGE_EVENTS.DRAG_START, STAGE_EVENTS.DRAG, STAGE_EVENTS.DRAG_END];
    stage.addEventListener(STAGE_EVENTS.POINTER_MOVE, onPointerMove);
    stage.addEventListener(STAGE_EVENTS.DRAG, onDrag);
    stageEvents.forEach(stageEvent => {
      addInterceptor(stageEvent, updateMousePosWithSnapPos);
    });
    return () => {
      stage.removeEventListener(STAGE_EVENTS.POINTER_MOVE, onPointerMove);
      stage.removeEventListener(STAGE_EVENTS.DRAG, onDrag);
      stageEvents.forEach(stageEvent => {
        removeInterceptor(stageEvent, updateMousePosWithSnapPos);
      });
    };
  }, [stage, onPointerMove, onDrag, addInterceptor, updateMousePosWithSnapPos, removeInterceptor, isLabelingMode]);
  return <svg height="100%" width="100%">
      {selectedTool !== TOOL.POINTER && onStage && <>
          {hintLine.pointX !== null && <Line data-testid="vertical-line" x1={hintLine.pointX} y1="0" x2={hintLine.pointX} y2="100%" $color="rgb(255,255,255,0.5)"></Line>}
          {hintLine.pointY !== null && <Line data-testid="horizontal-line" x1="0" y1={hintLine.pointY} x2="100%" y2={hintLine.pointY} $color="rgb(255,255,255,0.5)"></Line>}
        </>}
    </svg>;
};
export default Snapping;